import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useCountry } from '../../hooks/useCountry';

const API_URL = import.meta.env.VITE_API_URL || '/api/v1';

const Contact = () => {
  const { theme, isSubdomain } = useCountry();
  const { t } = useTranslation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const colors = theme?.colors || {};
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: 'general',
    message: '',
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      await axios.post(`${API_URL}/contact/`, formData);
      setSuccess(true);
      setFormData({ name: '', email: '', subject: 'general', message: '' });
    } catch (err) {
      setError('Failed to send message. Please try again or email us directly.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={isSubdomain ? '' : 'min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100'}>
      {/* Header - only on main domain */}
      {!isSubdomain && (
        <header className="bg-white/80 backdrop-blur-md shadow-sm sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-4 py-3 sm:py-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center">
              <Link to="/" className="flex-shrink-0">
                <h1 className="text-xl sm:text-2xl font-bold text-blue-600">RentyDoc</h1>
              </Link>
              <div className="hidden md:flex items-center space-x-4">
                <Link to="/about" className="text-gray-600 hover:text-gray-900 px-3 py-2 text-sm font-medium">About</Link>
                <Link to="/contact" className="text-blue-600 px-3 py-2 text-sm font-medium">Contact</Link>
                <Link to="/login" className="text-gray-700 hover:text-gray-900 px-3 py-2 text-sm font-medium">Login</Link>
                <Link to="/register" className="bg-blue-600 text-white hover:bg-blue-700 px-4 py-2 rounded-lg text-sm font-medium transition-colors">Sign Up</Link>
              </div>
              <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="md:hidden p-2 rounded-lg text-gray-600 hover:text-gray-900 hover:bg-gray-100">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={mobileMenuOpen ? "M6 18L18 6M6 6l12 12" : "M4 6h16M4 12h16M4 18h16"} />
                </svg>
              </button>
            </div>
            {mobileMenuOpen && (
              <div className="md:hidden mt-4 pb-4 border-t border-gray-100 pt-4 space-y-2">
                <Link to="/about" className="block px-4 py-3 text-gray-700 hover:bg-gray-50 rounded-lg font-medium" onClick={() => setMobileMenuOpen(false)}>About</Link>
                <Link to="/contact" className="block px-4 py-3 text-blue-600 bg-blue-50 rounded-lg font-medium" onClick={() => setMobileMenuOpen(false)}>Contact</Link>
                <Link to="/login" className="block px-4 py-3 text-gray-700 hover:bg-gray-50 rounded-lg font-medium" onClick={() => setMobileMenuOpen(false)}>Login</Link>
                <Link to="/register" className="block px-4 py-3 bg-blue-600 text-white text-center rounded-lg font-medium" onClick={() => setMobileMenuOpen(false)}>Sign Up</Link>
              </div>
            )}
          </div>
        </header>
      )}

      {/* Hero */}
      <section className="text-white py-10 sm:py-14 lg:py-16" style={{ background: theme ? colors.heroBg : 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)' }}>
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-2 sm:mb-3">{t('pages.contact.title')}</h1>
          <p className="text-base sm:text-lg text-white/80">
            {t('pages.contact.subtitle')}
          </p>
        </div>
      </section>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-8 sm:py-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 lg:gap-12">
          {/* Contact Form */}
          <div className="order-2 lg:order-1">
            <h2 className="text-xl sm:text-2xl font-semibold text-gray-900 mb-4 sm:mb-6">{t('pages.contact.formTitle')}</h2>
            {success ? (
              <div className="bg-white/70 backdrop-blur-sm rounded-2xl p-6 sm:p-8 border border-white/50 shadow-sm text-center">
                <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: `${colors.primary || '#16a34a'}15` }}>
                  <svg className="w-8 h-8" style={{ color: colors.primary || '#16a34a' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('pages.contact.successTitle')}</h3>
                <p className="text-gray-600 mb-6 text-sm sm:text-base">{t('pages.contact.successText')}</p>
                <button onClick={() => setSuccess(false)} className="font-medium" style={{ color: colors.primary || '#2563eb' }}>
                  {t('pages.contact.sendAnother')}
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="bg-white/70 backdrop-blur-sm rounded-2xl p-5 sm:p-8 border border-white/50 shadow-sm space-y-4 sm:space-y-5">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">{t('pages.contact.name')} *</label>
                  <input type="text" name="name" value={formData.name} onChange={handleChange} required className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:border-transparent text-base bg-white" style={{ '--tw-ring-color': colors.primary || '#3b82f6' }} placeholder="John Smith" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">{t('pages.contact.email')} *</label>
                  <input type="email" name="email" value={formData.email} onChange={handleChange} required className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base bg-white" placeholder="john@example.com" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">{t('pages.contact.subject')} *</label>
                  <select name="subject" value={formData.subject} onChange={handleChange} className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base bg-white appearance-none" style={{ backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`, backgroundPosition: 'right 0.75rem center', backgroundRepeat: 'no-repeat', backgroundSize: '1.5em 1.5em', paddingRight: '2.5rem' }}>
                    <option value="general">{t('pages.contact.subjects.general')}</option>
                    <option value="support">{t('pages.contact.subjects.support')}</option>
                    <option value="billing">{t('pages.contact.subjects.billing')}</option>
                    <option value="refund">{t('pages.contact.subjects.refund')}</option>
                    <option value="legal">{t('pages.contact.subjects.legal')}</option>
                    <option value="partnership">{t('pages.contact.subjects.partnership')}</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">{t('pages.contact.message')} *</label>
                  <textarea name="message" value={formData.message} onChange={handleChange} required rows={4} className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent text-base bg-white resize-none" placeholder="" />
                </div>
                {error && (
                  <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700 text-sm flex items-start gap-3">
                    <svg className="w-5 h-5 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span>{error}</span>
                  </div>
                )}
                <button type="submit" disabled={loading} className="w-full px-6 py-3.5 text-white rounded-xl font-semibold disabled:opacity-50 disabled:cursor-not-allowed transition-all active:scale-[0.98] shadow-lg" style={{ background: theme ? colors.buttonBg : 'linear-gradient(135deg, #6366f1, #8b5cf6)' }}>
                  {loading ? (
                    <span className="flex items-center justify-center gap-2">
                      <svg className="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      {t('pages.contact.sending')}
                    </span>
                  ) : t('pages.contact.send')}
                </button>
              </form>
            )}
          </div>

          {/* Contact Info */}
          <div className="order-1 lg:order-2">
            <h2 className="text-xl sm:text-2xl font-semibold text-gray-900 mb-4 sm:mb-6">{t('pages.contact.otherWays')}</h2>
            <div className="space-y-4">
              <div className="bg-white/70 backdrop-blur-sm rounded-2xl p-5 sm:p-6 border border-white/50 shadow-sm">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg" style={{ background: theme ? colors.buttonBg : 'linear-gradient(135deg, #3b82f6, #2563eb)' }}>
                    <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">{t('pages.contact.emailTitle')}</h3>
                    <p className="text-gray-600 text-sm mb-2">{t('pages.contact.emailDesc')}</p>
                    <a href="mailto:support@rentydoc.com" className="font-medium" style={{ color: colors.primary || '#2563eb' }}>support@rentydoc.com</a>
                  </div>
                </div>
              </div>

              <div className="bg-white/70 backdrop-blur-sm rounded-2xl p-5 sm:p-6 border border-white/50 shadow-sm">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg" style={{ background: theme ? colors.buttonBg : 'linear-gradient(135deg, #6366f1, #4f46e5)' }}>
                    <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">{t('pages.contact.responseTitle')}</h3>
                    <p className="text-gray-600 text-sm">{t('pages.contact.responseDesc')}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white/70 backdrop-blur-sm rounded-2xl p-5 sm:p-6 border border-white/50 shadow-sm">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg" style={{ background: theme ? colors.buttonBg : 'linear-gradient(135deg, #10b981, #059669)' }}>
                    <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">{t('pages.contact.faqTitle')}</h3>
                    <p className="text-gray-600 text-sm mb-2">{t('pages.contact.faqDesc')}</p>
                    <Link to="/faq" className="font-medium text-sm" style={{ color: colors.primary || '#2563eb' }}>{t('pages.contact.faqLink')} &rarr;</Link>
                  </div>
                </div>
              </div>

              <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-2xl p-5 sm:p-6 border border-amber-100">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-gradient-to-br from-amber-500 to-orange-500 rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg shadow-amber-500/25">
                    <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-1">{t('pages.contact.urgentTitle')}</h3>
                    <p className="text-gray-700 text-sm">{t('pages.contact.urgentDesc')}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer - only on main domain */}
      {!isSubdomain && (
        <footer className="bg-white border-t border-gray-200 mt-8 sm:mt-12">
          <div className="max-w-4xl mx-auto px-4 py-8 sm:py-10">
            <div className="flex flex-wrap justify-center gap-4 sm:gap-6 mb-6">
              <Link to="/terms" className="text-gray-600 hover:text-gray-900 text-sm">Terms</Link>
              <Link to="/privacy" className="text-gray-600 hover:text-gray-900 text-sm">Privacy</Link>
              <Link to="/about" className="text-gray-600 hover:text-gray-900 text-sm">About</Link>
              <Link to="/contact" className="text-blue-600 text-sm font-medium">Contact</Link>
            </div>
            <p className="text-gray-500 text-sm text-center">&copy; 2025 RentyDoc. All rights reserved.</p>
          </div>
        </footer>
      )}
    </div>
  );
};

export default Contact;
